import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Breadcrumbs } from '@/components/ui/breadcrumb'

import { Loading } from '../../../components/Loading'
import { Spinner } from '../../../components/Spinner'
import { useApiClient } from '../../../utils/api/ApiClient'
import { useCallbackWithForceRefresh } from '../../../utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from '../../../utils/api/useFetch'
import { WalletsApiClient } from '../../../utils/api/walletsApiClient'
import { links, marketingBonusCampaignDetailsRouteUrl } from '../../../utils/featureToggles'
import PageNotFound from '../../PageNotFound/PageNotFound'
import BonusCampaignDetailsCountryPage from './BonusCampaignDetailsCountryPage'
import BonusCampaignDetailsPage from './BonusCampaignDetailsPage'

export enum BonusCampaignTab {
  DETAILS = 'details',
  PARTICIPATING_COUNTRIES = 'participating-countries',
}

const ResolveBonusCampaignRoute = (): JSX.Element => {
  const walletsApiClient = useApiClient(WalletsApiClient)

  const [crumbs, setCrumbs] = useState([
    { title: 'Marketing Bonus Campaigns', link: links.bonusModule.url },
  ])

  const { id, slug } = useParams<{ id: string; slug: BonusCampaignTab }>()
  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(
    () => (id ? walletsApiClient.getBonusCampaignById(id) : Promise.resolve(null)),
    [id]
  )
  const { data, isLoading } = useFetchOne(fetchData)

  const matchesSlug = (leadDetailsSlug?: BonusCampaignTab) => {
    if (!leadDetailsSlug) {
      return false
    }
    return (
      leadDetailsSlug === BonusCampaignTab.DETAILS ||
      leadDetailsSlug === BonusCampaignTab.PARTICIPATING_COUNTRIES
    )
  }

  useEffect(() => {
    if (data) {
      const [crumb1, crumb2] = [
        ...crumbs,
        {
          title: data.name,
          link: marketingBonusCampaignDetailsRouteUrl(
            data,
            slug ? (matchesSlug(slug) ? slug : BonusCampaignTab.DETAILS) : BonusCampaignTab.DETAILS
          ),
        },
      ]
      setCrumbs([crumb1, crumb2])
    }
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const innerRender = () => {
    if (!matchesSlug(slug)) {
      return <PageNotFound />
    }
    if (isLoading) {
      return <Spinner />
    }
    if (!data) {
      return <PageNotFound />
    }
    if (slug === BonusCampaignTab.DETAILS) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <BonusCampaignDetailsPage data={data} afterSubmit={forceRefresh} />
        </React.Fragment>
      )
    }
    if (slug === BonusCampaignTab.PARTICIPATING_COUNTRIES) {
      return (
        <React.Fragment>
          <Breadcrumbs crumbs={crumbs} />
          <BonusCampaignDetailsCountryPage data={data} afterSubmit={forceRefresh} />
        </React.Fragment>
      )
    }
    return <PageNotFound />
  }

  return <Loading isLoading={isLoading && !matchesSlug(slug)}>{innerRender()}</Loading>
}

export default ResolveBonusCampaignRoute
